import {
  DestroyRef,
  type EnvironmentProviders,
  inject,
  Injector,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, ResolveEnd, Router } from '@angular/router';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { AuthService } from '@frontendlib/common/services/auth.service';
import {
  isProductAnalyticsConsentFromAnAccount,
  markProductAnalyticsConsentFromAnAccount,
  type ProductAnalyticsConsent,
} from '@frontendlib/common/services/product-analytics/product-analytics-consent';
import { ProductAnalyticsService } from '@frontendlib/common/services/product-analytics/product-analytics.service';
import { AccountSettingsService } from '@modules/account/services/account-settings.service';
import { MAIN_SPACE_CONTEXT } from '@modules/common/injection-tokens/space-context-key.injection-token';
import { ActiveSpaceContextService } from '@modules/common/services/active-space-context.service';
import { Application } from '@modules/common/types/application';
import { EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  retry,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

/** The Stitch app each top-level path belongs to (the `pages-routing.module.ts` paths). */
const APP_BY_PATH: Readonly<Record<string, Application>> = {
  mail: Application.mail,
  chat: Application.chat,
  events: Application.calendar,
  tasks: Application.tasking,
  notes: Application.notes,
  sheets: Application.sheets,
  slides: Application.slides,
  contacts: Application.contacts,
  files: Application.files,
  settings: Application.settings,
  admin: Application.admin,
};

/** The Stitch app a URL is in, reading past a `/spaces/<id>/` prefix; null outside any app. */
export function stitchAppForUrl(url: string): Application | null {
  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
  const first = segments[0] === 'spaces' ? segments[2] : segments[0];
  return (first && APP_BY_PATH[first]) || null;
}

/**
 * Pages whose address holds a secret (an invitation token) or a public share link's key, and that show content
 * outside the blocked areas. Replay uploads send the page address as it is, so analytics does not run there.
 */
const UNTRACKED_PAGES = /^\/(join|shared)(\/|$)/;

/** A failed save of an answer is tried again this many times, this far apart, before the person is told. */
const SAVE_RETRIES = 2;
const SAVE_RETRY_DELAY_MS = 2000;

/** This browser's answer as the account stores it. */
function asAccountConsent(consent: ProductAnalyticsConsent | null): boolean | null {
  return consent === null ? null : consent === 'accepted';
}

/**
 * The account wins. Without an answer of its own, this browser's applies, unless it belongs to another account on
 * the device (added or switched to), and the banner asks if there is none. Settings that could not be read leave
 * analytics off when the browser's answer is another account's: it may not be this one's.
 */
function applyAccountConsent(analytics: ProductAnalyticsService, saved: boolean | null | undefined): void {
  if (saved === true) {
    analytics.optIn();
    markProductAnalyticsConsentFromAnAccount();
    return;
  }
  if (saved === false) {
    analytics.optOut();
    markProductAnalyticsConsentFromAnAccount();
    return;
  }
  if (isProductAnalyticsConsentFromAnAccount()) {
    if (saved === undefined) return;
    analytics.forgetConsent();
  }
  analytics.start();
  analytics.askForConsent();
}

/**
 * Starts product analytics in the main app, the only app that runs it (the login pages carry reset and
 * verification tokens in their addresses): signed in, the account's answer decides first. Also what only the main app knows,
 * handed to it: the signed-in account (as the server just returned it, not the stored copy, which can still name
 * the previous account right after a switch), the space in the left pane, and the Stitch app the page is in.
 */
export function provideMainProductAnalytics(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer((): void => {
      const analytics = inject(ProductAnalyticsService);
      if (!analytics.isEnabled() || UNTRACKED_PAGES.test(window.location.pathname)) return;
      const destroyRef = inject(DestroyRef);

      if (inject(AuthService).isAuthenticated()) {
        inject(AccountService)
          .getAccount()
          .pipe(
            map((account) => account.id),
            distinctUntilChanged(),
            takeUntilDestroyed(destroyRef),
          )
          .subscribe((accountId) => analytics.identify(accountId));

        // The account's answer decides before anything loads, so a device never tracks someone whose account
        // declined. Afterwards, any answer that differs from the account's (this browser's earlier one, the
        // banner, the settings switch) is saved to the account.
        const accountSettings = inject(AccountSettingsService);
        const injector = inject(Injector);
        const toaster = inject(ToasterService);
        accountSettings
          .readAnalyticsConsent()
          .pipe(
            switchMap((saved) => {
              applyAccountConsent(analytics, saved);
              // Unread (a failed request): only a new answer is saved, never this browser's older one.
              const baseline = saved === undefined ? asAccountConsent(analytics.consent()) : saved;
              // Watched only from here: an observer made at start-up would first replay the answer this browser had
              // before the account's applied (another account's, say) and save it.
              return toObservable(analytics.consent, { injector }).pipe(
                map(asAccountConsent),
                startWith(baseline),
                distinctUntilChanged(),
                skip(1),
              );
            }),
            filter((answer): answer is boolean => answer !== null),
            concatMap((answer) =>
              accountSettings.writeAnalyticsConsent(answer).pipe(
                // An unsaved answer loses to the account's older one at the next start: try again, then say so.
                retry({ count: SAVE_RETRIES, delay: SAVE_RETRY_DELAY_MS }),
                tap(() => markProductAnalyticsConsentFromAnAccount()),
                catchError(() => {
                  toaster.show({ text: "We couldn't save your analytics choice. Please try again." });
                  return EMPTY;
                }),
              ),
            ),
            takeUntilDestroyed(destroyRef),
          )
          .subscribe();
      } else {
        analytics.start();
      }

      inject(ActiveSpaceContextService)
        .contextId$(MAIN_SPACE_CONTEXT)
        .pipe(distinctUntilChanged(), takeUntilDestroyed(destroyRef))
        .subscribe((spaceId) => analytics.setSpace(spaceId ?? null));

      // Set before the address changes (`ResolveEnd` runs once the guards have passed), because heatmaps count
      // the page view on the address change itself; at start-up the address is already the page's.
      analytics.setApp(stitchAppForUrl(window.location.pathname));
      const router = inject(Router);
      router.events
        .pipe(
          filter((event): event is ResolveEnd => event instanceof ResolveEnd),
          map((event) => stitchAppForUrl(event.urlAfterRedirects)),
          distinctUntilChanged(),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe((app) => analytics.setApp(app));
      // Our page view, once the page is shown; the address's path only, never its query string.
      router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe((event) => analytics.pageViewed(new URL(event.urlAfterRedirects, window.location.origin).pathname));
    }),
  ]);
}
